import { motion } from "framer-motion";
import { CheckCircle2, Copy, PartyPopper, X, Info, AlertCircle } from "lucide-react";
import React from "react";

/**
 * Toast notification component
 * @param {Object} props
 * @param {string} props.message - The message to display
 * @param {string} props.type - The type of toast (success, error, celebration, copy, info)
 * @param {Function} props.onClose - Function to call when closing the toast
 * @param {boolean} [props.stacked] - When true, the toast does NOT self-position
 *   (no `fixed bottom-right`); the parent (Toaster) owns positioning/stacking.
 *   Defaults to false so existing standalone callers are unaffected.
 * @returns {JSX.Element}
 */
const Toast = ({ message, type, onClose, stacked = false }) => {
  // Determine which icon to show based on the toast type
  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle2 className="tw-h-5 tw-w-5 tw-text-emerald-500" />;
      case "error":
        return <AlertCircle className="tw-h-5 tw-w-5 tw-text-red-500" />;
      case "celebration":
        return <PartyPopper className="tw-h-5 tw-w-5 tw-text-amber-500" />;
      case "copy":
        return <Copy className="tw-h-5 tw-w-5 tw-text-emerald-500" />;
      case "info":
        return <Info className="tw-h-5 tw-w-5 tw-text-blue-500" />;
      default:
        return null;
    }
  };

  // Determine the position based on the toast type (standalone mode only).
  const position = type === "info" ? "tw-left-4" : "tw-right-4";

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className={stacked ? "" : `tw-fixed tw-bottom-4 ${position} tw-z-50`}
    >
      <div className="tw-flex tw-items-center tw-space-x-3 tw-rounded-xl tw-bg-white tw-p-4 tw-shadow-lg">
        {getIcon()}
        <p className={`${type === "info" ? "tw-text-sm tw-text-gray-500" : "tw-text-gray-700"}`}>
          {message}
        </p>
        <button onClick={onClose} className="tw-rounded-full tw-p-1 hover:tw-bg-gray-100">
          <X className="tw-h-4 tw-w-4 tw-text-gray-500" />
        </button>
      </div>
    </motion.div>
  );
};

export default Toast;
